import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import SubjectTypeTile from '@Guidelines/Component/SubjectTypeTile';
import resolveSubjectTypeColor from '@Guidelines/Component/SubjectTypeTile/Helpers/resolveSubjectTypeColor';
import describeSubjectCount from '@UI/Pages/Home/SubjectTypesBlock/Helpers/describeSubjectCount';
import {
	buildCardStyle,
	cardFacilityStyle,
	cardFigureStyle,
	cardTextStyle,
	cardTitleStyle,
	chevronStyle,
} from '@UI/Pages/Home/Shared/shortcutCardStyles';
import type SubjectTypeView from '@UI/Store/SubjectTypes/Views/SubjectTypeView';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@verclam/ui-kit';
import { ChevronRight } from 'lucide-react';

type Props = {
	type: SubjectTypeView;
	count: number | null;
	isLoading: boolean;
	scopeLabel: string;
};

/** Link card of one dossier type: its tile, its plural name, the number of dossiers and the scope. */
const SubjectTypeShortcutCard: React.FC<Props> = ({ type, count, isLoading, scopeLabel }) => {
	const cardStyle = React.useMemo(
		() => buildCardStyle(resolveSubjectTypeColor(type.color.value)),
		[type.color.value],
	);
	const figure = count === null ? null : describeSubjectCount(count);
	const ariaLabel =
		figure === null ? undefined : `${type.wording.plural} : ${figure.toLowerCase()}, ${scopeLabel}`;

	return (
		<Link to={buildSubjectListPath(type.key)} css={cardStyle} aria-label={ariaLabel}>
			<SubjectTypeTile icon={type.icon.value} color={type.color.value} />
			<span css={cardTextStyle}>
				<span css={cardTitleStyle}>{type.wording.plural}</span>
				{isLoading ? (
					<Skeleton width="6rem" height="1.097rem" />
				) : (
					<ConditionalLoader data={figure}>
						{(text) => <span css={cardFigureStyle}>{text}</span>}
					</ConditionalLoader>
				)}
				<span css={cardFacilityStyle}>{scopeLabel}</span>
			</span>
			<span css={chevronStyle} aria-hidden="true">
				<ChevronRight size={16} />
			</span>
		</Link>
	);
};

export default SubjectTypeShortcutCard;
